import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { TableUtility } from '@utility/dynamic-table';
import { useAuth } from '../../../Hooks/useAuth';
import EmployeeProfileHeader from './components/EmployeeProfileHeader/employeeProfileHeader';
import normalizeTableUtilityResponse from '../../../Utils/tableUtilityResponse';
import './ResourceManagement.scss';
import {
  getResourceMgmtEmployeeFilterReq,
  resourceManagementBaseURL,
} from '../../../Api/request/resourceManagement';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

const toOption = (option) => {
  if (option && typeof option === 'object') {
    const label = option.label ?? option.name ?? option.value ?? '';
    const value = option.value ?? option.id ?? label;

    return { label, value };
  }

  return {
    label: option,
    value: option,
  };
};

const getFilterSource = (source) => (
  Array.isArray(source) ? { values: source } : source || {}
);

/* -------------------------------------------------------------------------- */
// Helper function to build choices for filters
/* -------------------------------------------------------------------------- */
const buildChoiceFilter = (field, label, source, template = 'multiSelect') => {
  const { values = [], url } = getFilterSource(source);
  const options = values.map(toOption);

  return {
    type: 'filter',
    field,
    label,
    placeholder: `Select ${label}`,
    template,
    ...(template === 'singleSelect'
      ? { selectedValue: null }
      : { selectedValues: [] }),
    ...(url ? { url } : {}),
    options,
    values: options,
  };
};

const filterBaseUrl = getResourceMgmtEmployeeFilterReq().url;

// Define column configuration specifically for the table
const tableConfig = [
  {
    field: 'projectName',
    header: 'Project Name',
    width: 180,
    filter: true,
    visible: true,
    sortable: false,
    pinnable: false,
    order: 1,
    defaultPinned: false,
    isGlobalFilter: true,
    showFilterButtonInHeader: false,
    showPinButtonInHeader: false,
    menuItems: [
      {
        label: 'Project Name',
        items: [
          buildChoiceFilter('projectName', 'Project Name', []),
        ],
      },
    ],
  },
  {
    field: 'role',
    header: 'Role',
    width: 120,
    filter: true,
    visible: true,
    sortable: false,
    pinnable: false,
    order: 2,
    defaultPinned: false,
    isGlobalFilter: true,
    showFilterButtonInHeader: false,
    showPinButtonInHeader: false,
    menuItems: [
      {
        label: 'Role',
        items: [
          buildChoiceFilter('role', 'Role', []),
        ],
      },
    ],
  },
  {
    field: 'allocationPercent',
    header: 'Allocation %',
    align: 'right',
    width: 130,
    filter: false,
    visible: true,
    sortable: false,
    pinnable: false,
    order: 3,
    defaultPinned: false,
    showFilterButtonInHeader: false,
    showPinButtonInHeader: false,
  },
  {
    field: 'allocationStartDate',
    header: 'Allocation Start Date',
    width: 200,
    filter: false,
    visible: true,
    sortable: false,
    pinnable: false,
    order: 4,
    defaultPinned: false,
    showFilterButtonInHeader: false,
    showPinButtonInHeader: false,
  },
  {
    field: 'allocationEndDate',
    header: 'Allocation End Date',
    width: 160,
    filter: false,
    visible: true,
    sortable: false,
    pinnable: false,
    order: 5,
    defaultPinned: false,
    showFilterButtonInHeader: false,
    showPinButtonInHeader: false,
  },
  {
    field: 'comments',
    header: 'Comments',
    width: 250,
    filter: false,
    visible: true,
    sortable: false,
    pinnable: false,
    order: 6,
    defaultPinned: false,
    showFilterButtonInHeader: false,
    showPinButtonInHeader: false,
  },
];

const resourceManagementExportColumns = [
  { field: 'project_name', header: 'project_name', width: 180 },
  { field: 'emp_id', header: 'emp_id', width: 120 },
  { field: 'name', header: 'name', width: 180 },
  { field: 'resource_type', header: 'resource_type', width: 160 },
  { field: 'allocation', header: 'allocation', width: 100 },
  { field: 'skills', header: 'skills', width: 180 },
  { field: 'start_date', header: 'start_date', width: 120 },
  { field: 'end_date', header: 'end_date', width: 120 },
  { field: 'efforts', header: 'efforts', width: 100 },
  { field: 'remarks', header: 'remarks', width: 180 },
];

const normalizeRMExportRows = (rows = []) => (
  (Array.isArray(rows) ? rows : []).map((row) => ({
    project_name: row.project_name || row.projectName || '',
    emp_id: row.emp_id || row.empId || row.employeeId || '',
    name: row.name || row.resourceName || `${row.first_name || ''} ${row.last_name || ''}`.trim(),
    resource_type: row.resource_type || row.resource_type_name || row.resourceType || '',
    allocation: row.allocation ?? row.billability ?? row.efforts ?? 0,
    skills: row.skills || '',
    start_date: row.start_date || row.startDate || '',
    end_date: row.end_date || row.endDate || '',
    efforts: row.efforts ?? 0,
    remarks: row.remarks || '-',
  }))
);

const getToolbarConfiguration = ({
  userProfile,
  tokenVal,
  toolbarConfigState,
  employeeDetailsRef,
  onFilteredResponse,
  onSetTableState,
}) => ({
  generalSettings: {
    enableApiFiltering: true,
    baseURL: resourceManagementBaseURL,
    showToolBar: true,
    showPagination: false,
    userInfo: {
      emailId: userProfile?.email || '',
      permissions: userProfile?.permissions || [],
      roleDetails: userProfile?.roleDetails || {},
      employeeId: userProfile?.employeeId || '',
    },
    headers: { token: tokenVal },
    additionalPayloadData: {
      get selectedEmployeeEmail() {
        return employeeDetailsRef.current?.emailId || '';
      },
    },
    apiEndpoints: {
      getAllViews: { endpoint: '', enabled: false },
      getFilteredData: { endpoint: '', enabled: true },
      saveOrUpdateView: { endpoint: '', enabled: false },
      deleteView: { endpoint: '', enabled: false },
      download: {
        endpoint: 'export',
        enabled: true,
        filename: () => {
          const empName = employeeDetailsRef.current?.fullName
            ? employeeDetailsRef.current.fullName.trim().replace(/\s+/g, '_')
            : 'Default';
          return `ResourceManagement_${empName}`;
        },
        sheetName: () => 'ResourceManagement',
        reportTitle: () => {
          const empName = employeeDetailsRef.current?.fullName
            ? ` (${employeeDetailsRef.current.fullName.trim()})`
            : '';
          return `Resource Management${empName}`;
        },
        transformResponse: (payload) => {
          const rows = Array.isArray(payload?.tableData)
            ? payload.tableData
            : Array.isArray(payload?.data?.tableData)
              ? payload.data.tableData
              : Array.isArray(payload?.data)
                ? payload.data
                : Array.isArray(payload)
                  ? payload
                  : [];

          return {
            tableConfiguration: payload?.tableConfiguration || resourceManagementExportColumns,
            tableData: normalizeRMExportRows(rows),
          };
        },
      },
    },
    toolBarSettings: {
      showAddNew: false,
      showDownloadExcel: true,
      showColumnCustomize: false,
      showColumnFilterMenu: true,
      showHelp: false,
      showSearch: false,
      showSaveView: false,
      showReset: false,
      showExpandCollapse: false,
      showFilterOption: true,
      showAppliedFilters: true,
      showToolBarHeading: true,
      toolBarHeading: 'Resource Management',
    },
    handler: {
      handleFilteredApiResponse: onFilteredResponse,
      setTableStateHandler: onSetTableState,
    },
  },
  tableSettings: {
    size: 'small',
    pageSize: 10,
    tableConfig: toolbarConfigState,
    tableScrollHeight: '400px',
    loaderSkeleton: { rows: 5, bodyCells: 2, columns: 4 },
    paginationConfig: { page: 1, limit: 10 },
    showExpandCollapse: false,
    maxFrozenColumns: { desktop: 2, tablet: 2, mobile: 1 },
  },
});

const getTableConfigurationForUtility = ({ userProfile, tableConfigState }) => ({
  generalSettings: {
    enableApiFiltering: false,
    baseURL: '',
    showToolBar: false,
    showPagination: true,
    userInfo: {
      emailId: userProfile?.email || '',
      permissions: userProfile?.permissions || [],
      roleDetails: userProfile?.roleDetails || {},
      employeeId: userProfile?.employeeId || '',
    },
    headers: {},
    apiEndpoints: {
      getAllViews: { endpoint: '', enabled: false },
      getFilteredData: { endpoint: '', enabled: false },
      saveOrUpdateView: { endpoint: '', enabled: false },
      deleteView: { endpoint: '', enabled: false },
      download: { endpoint: '', enabled: false },
    },
    toolBarSettings: {
      showAddNew: false,
      showDownloadExcel: false,
      showColumnCustomize: false,
      showColumnFilterMenu: false,
      showHelp: false,
      showSearch: false,
      showSaveView: false,
      showReset: false,
      showExpandCollapse: false,
      showFilterOption: false,
      showToolBarHeading: false,
      toolBarHeading: '',
    },
  },
  tableSettings: {
    size: 'small',
    pageSize: 10,
    tableConfig: tableConfigState,
    tableScrollHeight: 'auto',
    loaderSkeleton: { rows: 5, bodyCells: 6, columns: 6 },
    paginationConfig: { page: 1, limit: 10 },
    showExpandCollapse: false,
    maxFrozenColumns: { desktop: 2, tablet: 2, mobile: 1 },
  },
});

const handleFilteredApiResponseBase = (response, handlers = {}) => {
  const {
    setLoadingEmployee = () => {},
    setTableData = () => {},
    setEmployeeDetails = () => {},
    employeeDetailsRef,
    setTableConfigState = () => {},
    setToolbarConfigState = () => {},
  } = handlers;

  setLoadingEmployee(false);
  if (response?.success && response?.data) {
    if (!response.data.tableConfiguration && !response.data.toolbarTableConfig) {
      return response;
    }

    const rawGridConfig = response.data.tableConfiguration;
    let rawToolbarConfig = response.data.toolbarTableConfig;

    if (rawToolbarConfig) {
      const empDetails = response.data.employeeDetails || response.data.data?.employeeDetails;
      if (empDetails && empDetails.emailId) {
        rawToolbarConfig = rawToolbarConfig.map((col) => {
          if (col.field === 'employeeName' && col.menuItems?.[0]?.items?.[0]) {
            col.menuItems[0].items[0].selectedLabel = empDetails.fullName;
            col.menuItems[0].items[0].selectedValue = empDetails.emailId;
            col.menuItems[0].items[0].selectedValues = [empDetails.emailId];
          }
          return col;
        });
      }
      response.data.tableConfiguration = rawToolbarConfig;
    }

    const normalizedResponse = normalizeTableUtilityResponse(response);

    let normalizedGridConfig = rawGridConfig;
    if (rawGridConfig) {
      const tempGridResponse = { data: { tableConfiguration: rawGridConfig, tableData: [] } };
      const normalizedGridTemp = normalizeTableUtilityResponse(tempGridResponse);
      normalizedGridConfig = normalizedGridTemp.data.tableConfiguration;
    }

    if (normalizedResponse.data.tableData) {
      setTableData(normalizedResponse.data.tableData);
    }
    if (normalizedResponse.data.employeeDetails) {
      setEmployeeDetails(normalizedResponse.data.employeeDetails);
    } else if (Array.isArray(normalizedResponse.data.tableData)
      && normalizedResponse.data.tableData.length === 0) {
      if (employeeDetailsRef) employeeDetailsRef.current = null;
      setEmployeeDetails(null);
    }

    if (normalizedGridConfig) {
      setTableConfigState(normalizedGridConfig);
    }
    if (normalizedResponse.data.tableConfiguration) {
      setToolbarConfigState(normalizedResponse.data.tableConfiguration);
    }

    return normalizedResponse;
  }
  return response;
};

const toolbarTableConfig = [
  {
    field: 'employeeName',
    header: 'Employee Name',
    width: 150,
    filter: true,
    visible: true,
    sortable: false,
    pinnable: false,
    order: 1,
    defaultPinned: false,
    isGlobalFilter: true,
    showFilterButtonInHeader: false,
    showPinButtonInHeader: false,
    menuItems: [
      {
        label: 'Employee Name',
        items: [
          buildChoiceFilter('employeeName', 'Employee Name', {
            values: [],
            url: filterBaseUrl,
          }, 'singleSelect'),
        ],
      },
    ],
  },
];

// Main Resource Management component
/* -------------------------------------------------------------------------- */
const ResourceManagement = () => {
  const { userProfile, authToken } = useAuth();
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [tableConfigState, setTableConfigState] = useState(tableConfig);
  const [toolbarConfigState, setToolbarConfigState] = useState(toolbarTableConfig);
  const [toolbarResetNonce, setToolbarResetNonce] = useState(0);
  const tokenVal = localStorage.getItem('authToken') || userProfile?.access_token || authToken || '';
  const isLoaded = Boolean(tokenVal || userProfile?.email);
  const employeeDetailsRef = useRef(null);
  employeeDetailsRef.current = employeeDetails;

  const clearEmployeeSelection = useCallback(() => {
    employeeDetailsRef.current = null;
    setEmployeeDetails(null);
    setLoadingEmployee(false);
    setToolbarResetNonce((current) => current + 1);
  }, []);

  useEffect(() => {
    const handleToolbarClicks = (event) => {
      const target = event.target;
      if (!target?.closest) return;

      const isResetButton = Boolean(
        target.closest('.pi-refresh')
        || target.closest('button[aria-label*="Reset"]')
        || target.closest('button[title*="Reset"]'),
      );
      const isFilterReset = Boolean(
        target.closest('.tu-filter-footer-reset')
        || target.closest('button[aria-label*="Clear"]')
        || target.closest('button[title*="Clear"]'),
      );

      if (isResetButton || isFilterReset) {
        clearEmployeeSelection();
      }
    };

    document.addEventListener('click', handleToolbarClicks, true);
    return () => document.removeEventListener('click', handleToolbarClicks, true);
  }, [clearEmployeeSelection]);

  const handleFilteredApiResponse = useCallback((response) => {
    return handleFilteredApiResponseBase(response, {
      setLoadingEmployee,
      setTableData,
      setEmployeeDetails,
      employeeDetailsRef,
      setTableConfigState,
      setToolbarConfigState,
    });
  }, [
    employeeDetailsRef,
    setEmployeeDetails,
    setLoadingEmployee,
    setTableConfigState,
    setTableData,
    setToolbarConfigState,
  ]);

  // 1. Toolbar Only Configuration
  const toolbarConfiguration = useMemo(() => ({
    ...getToolbarConfiguration({
      userProfile,
      tokenVal,
      toolbarConfigState,
      employeeDetailsRef,
      onFilteredResponse: handleFilteredApiResponse,
      onSetTableState: () => {
        setLoadingEmployee(true);
      },
    }),
  }), [userProfile, tokenVal, toolbarConfigState]);


  // 2. Table Grid Only Configuration
  const tableConfigurationForUtility = useMemo(() => ({
    ...getTableConfigurationForUtility({ userProfile, tableConfigState }),
  }), [userProfile, tableConfigState]);

  if (!isLoaded) {
    return null;
  }

  return (
    <div className="resource-mgmt-wrapper">

      {/* Resource Management Bar */}
      <div className="resource-mgmt-table-utility table-utility toolbar-only">
        <TableUtility
          key={toolbarResetNonce}
          configuration={toolbarConfiguration}
          data={[]}
        />
      </div>

      {/* Profile Detail Cards */}
      <EmployeeProfileHeader employeeData={employeeDetails} loading={loadingEmployee} />

      {/* Allocation List Table */}
      <div className="resource-mgmt-table-utility table-utility table-only">
        <TableUtility
          configuration={tableConfigurationForUtility}
          data={tableData}
        />
      </div>
    </div>
  );
};

export default ResourceManagement;

